import {call, type Effect, type ForkEffect, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ApiActions} from '@touchnet/react-common';
import {TnToastActions} from '@touchnet/react-toast';
import LOG from 'loglevel';

import Selectors from '../selectors';
import {CommonWorktag, Worktag} from '~/types/common-worktag-type';
import {cast} from "~/utils/fns.ts";
import {actions} from "~/state/reducers/worktag-reducer";
import WorktagService from "~/services/worktag-service";

export function* loadWorktags(action: ReturnType<typeof actions.loadWorktags>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));

    try {
        const worktags: Worktag[] = yield call(WorktagService.loadWorktags, cNum, serverBase);

        if (worktags) {
            yield put(actions.receiveWorktags(worktags.map((worktag: Worktag) => worktag.code)));
        }
    } catch (error) {
        LOG.error('LOAD_WORKTAGS: Error calling the api %o', [error]);
        yield put(actions.receiveWorktagsFailed());
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export function* loadCommonWorktags(action: ReturnType<typeof actions.loadCommonWorktags>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));

    try {
        const commonWorktags: CommonWorktag[] = yield call(WorktagService.loadCommonWorktags, cNum, serverBase);

        if (commonWorktags) {
            yield put(actions.receiveCommonWorktags(commonWorktags));
        }
    } catch (error) {
        LOG.error('LOAD_COMMON_WORKTAGS: Error calling the api %o', [error]);
        yield put(actions.receiveCommonWorktagsFailed());
        yield put(TnToastActions.showErrorMessage("Error Loading Common Worktags"));
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export function* saveCommonWorktags(action: ReturnType<typeof actions.saveCommonWorktags>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));
    const currentCommonWorktags: CommonWorktag[] = yield select(Selectors.getCurrentCommonWorktags);
    const navigate = action.payload;

    if (Array.isArray(currentCommonWorktags)) {
        // Split into worktags to be updated, to be added, and to be deleted.
        const updateWorktags = [];
        const saveWorktags = [];
        const savedIds = [];
        const worktagIdsToDelete = [];

        currentCommonWorktags.forEach((commonWorktag: CommonWorktag) => {
            const updatedWorktag: CommonWorktag = {
                code: commonWorktag.code,
                required: commonWorktag.required,
                editable: commonWorktag.editable,
            };
            if (commonWorktag.toDelete) {
                if (commonWorktag.id) {
                    worktagIdsToDelete.push(commonWorktag.id);
                }
            } else if (commonWorktag.id) {
                updatedWorktag.id = commonWorktag.id;
                updateWorktags.push(updatedWorktag);
                savedIds.push(commonWorktag.id);
            } else {
                saveWorktags.push(updatedWorktag);
            }
        });

        try {
            if (/^C[0-9]{5}$/i.test(cNum)) {
                if (updateWorktags.length > 0) {
                    yield call(WorktagService.updateCommonWorktags, cNum, serverBase, updateWorktags);
                }
                if (saveWorktags.length > 0) {
                    yield call(WorktagService.saveCommonWorktags, cNum, serverBase, saveWorktags);
                }
                if (worktagIdsToDelete.length > 0) {
                    yield call(WorktagService.deleteCommonWorktags, cNum, serverBase, worktagIdsToDelete);
                }
                yield put(actions.commonWorktagsSaved());
                yield put(TnToastActions.showSuccessMessage('Common Worktags Saved'));
                yield call(navigate, `/${cNum}/ac/codes`);
            } else {
                yield put(TnToastActions.showErrorMessage('Error saving Common Worktags'));
                LOG.error(`Invalid customer number: ${cNum}`);
            }
        } catch (error) {
            LOG.error('SAVE_COMMON_WORKTAGS: Something went wrong while calling the api %o', [error]);
            yield put(TnToastActions.showErrorMessage('Error saving Common Worktags'));
        }
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export default function* rootSaga(): IterableIterator<ForkEffect> {
    yield takeEvery(actions.loadWorktags.type, loadWorktags);
    yield takeEvery(actions.loadCommonWorktags.type, loadCommonWorktags);
    yield takeEvery(actions.saveCommonWorktags.type, saveCommonWorktags);
}
